import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API, useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * Secure remaining-balance recovery page. Reached from the email link
 * /pay-balance/:token. The route is auth-gated (RequireAuth), so an
 * unauthenticated visitor is sent to /login?next=/pay-balance/:token and
 * returns here after signing in. The server independently verifies that the
 * signed-in account owns the booking the token points at.
 */

const REASON_COPY = {
  invalid: "This payment link is not valid. Open your BookFreely account to review your bookings.",
  expired: "This payment link has expired. Open your BookFreely account to complete the payment.",
  used: "This balance has already been paid. You're all set.",
  already_paid: "This balance has already been paid. You're all set.",
  already_authorized:
    "The final payment is already authorized — it's collected automatically when the business marks the appointment completed.",
  cancelled: "This booking was cancelled, so nothing is due.",
  declined: "This booking was declined, so nothing is due.",
  refunded: "This booking was refunded, so nothing is due.",
  expired_booking: "This booking has expired.",
  forbidden: "This booking belongs to a different account.",
};

export default function PayBalance() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const { user, authHeader, loading } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const [paying, setPaying] = useState(false);
  const justPaid = params.get("paid") === "1";
  const justCancelled = params.get("cancelled") === "1";

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/balance-recovery/${token}`, { headers: authHeader() });
      setState({ status: "ready", data: r.data, error: null });
    } catch (e) {
      const code = e?.response?.status;
      const msg = e?.response?.data?.detail || "We couldn't open this payment link.";
      setState({ status: "error", data: null, error: { code, msg } });
    }
  }, [token, authHeader]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      nav(`/login?next=${encodeURIComponent(`/pay-balance/${token}`)}`, { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line
  }, [loading, user?.id, token]);

  const pay = async () => {
    setPaying(true);
    try {
      const r = await axios.post(
        `${API}/balance-recovery/${token}/pay`,
        { origin_url: window.location.origin },
        { headers: authHeader() }
      );
      if (r.data?.url) {
        window.location.href = r.data.url;
        return;
      }
      toast.error("Could not start the payment. Please try again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the payment. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--gf-bg)" }}>
      <Header />
      <main className="flex-1 w-full max-w-lg mx-auto px-6 py-16" data-testid="pay-balance-page">
        {children}
      </main>
      <Footer />
    </div>
  );

  if (state.status === "loading") {
    return (
      <Shell>
        <div className="text-sm text-center" style={{ color: "var(--gf-text-muted)" }} data-testid="pay-balance-loading">
          Loading your payment details…
        </div>
      </Shell>
    );
  }

  if (state.status === "error") {
    const code = state.error?.msg?.toLowerCase().includes("different account") ? "forbidden" : null;
    return (
      <Shell>
        <div className="text-center" data-testid="pay-balance-unavailable">
          <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>
            Payment link
          </div>
          <h1 className="font-display text-3xl mt-3" style={{ color: "var(--gf-text)" }}>
            {code === "forbidden" ? "Wrong account" : "Link unavailable"}
          </h1>
          <p className="mt-3 text-sm" style={{ color: "var(--gf-text-muted)" }} data-testid="pay-balance-error-message">
            {state.error?.msg}
          </p>
          <button
            type="button"
            onClick={() => nav("/account")}
            className="mt-6 rounded-full px-5 py-2 text-sm font-medium"
            style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            data-testid="pay-balance-goto-account"
          >
            Go to my bookings
          </button>
        </div>
      </Shell>
    );
  }

  const d = state.data || {};

  // Not payable (already paid / cancelled / etc.) OR just returned from a
  // successful Stripe redirect.
  if (!d.payable || justPaid || d.reason === "already_paid") {
    const settled = justPaid || d.reason === "already_paid" || d.reason === "used";
    const copy = settled
      ? "Your payment is complete. You're all set for your appointment."
      : REASON_COPY[d.reason] || "This balance can no longer be paid here.";
    return (
      <Shell>
        <div className="text-center" data-testid="pay-balance-settled">
          <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>
            {d.business_name || "BookFreely"}
          </div>
          <h1 className="font-display text-3xl mt-3" style={{ color: "var(--gf-text)" }}>
            {settled ? "Payment complete" : "Nothing to pay"}
          </h1>
          <p className="mt-3 text-sm" style={{ color: "var(--gf-text-muted)" }} data-testid="pay-balance-settled-message">
            {copy}
          </p>
          <button
            type="button"
            onClick={() => nav("/account")}
            className="mt-6 rounded-full px-5 py-2 text-sm font-medium"
            style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            data-testid="pay-balance-goto-account"
          >
            Go to my bookings
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>
        Complete your payment
      </div>
      <h1 className="font-display text-3xl mt-3" style={{ color: "var(--gf-text)" }}>
        Remaining balance
      </h1>
      {justCancelled && (
        <p className="mt-3 text-sm" style={{ color: "var(--gf-accent)" }} data-testid="pay-balance-cancelled-note">
          Payment was cancelled — you can try again below whenever you're ready.
        </p>
      )}

      <div
        className="mt-6 rounded-2xl p-6"
        style={{ border: "1px solid var(--gf-border-strong)", background: "var(--gf-surface)" }}
        data-testid="pay-balance-summary"
      >
        <Row label="Business" value={d.business_name} testid="pay-balance-business" />
        <Row label="Service" value={d.service_title} testid="pay-balance-service" />
        {d.appointment_display && (
          <Row label="Appointment" value={d.appointment_display} testid="pay-balance-appointment" />
        )}
        {d.saved_card_brand && d.saved_card_last4 && (
          <Row
            label="Card on file"
            value={`${String(d.saved_card_brand).toUpperCase()} ···· ${d.saved_card_last4}`}
            testid="pay-balance-card"
          />
        )}
        <div className="flex items-center justify-between pt-4 mt-4" style={{ borderTop: "1px solid var(--gf-border)" }}>
          <span className="text-sm font-medium" style={{ color: "var(--gf-text)" }}>
            Amount due now
          </span>
          <span className="font-display text-2xl" style={{ color: "var(--gf-text)" }} data-testid="pay-balance-amount">
            {d.outstanding_display}
          </span>
        </div>
        <p className="mt-2 text-xs" style={{ color: "var(--gf-text-muted)" }}>
          This is the outstanding service balance only. The Booking &amp; Processing Fee was already paid and will not be charged again.
        </p>
      </div>

      <button
        type="button"
        onClick={pay}
        disabled={paying}
        className="mt-6 w-full rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-50"
        style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
        data-testid="pay-balance-pay-btn"
      >
        {paying ? "Opening secure checkout…" : `Pay ${d.outstanding_display} securely`}
      </button>
      <p className="mt-3 text-xs text-center" style={{ color: "var(--gf-text-muted)" }}>
        You'll complete the payment on Stripe's secure checkout. You can use your saved card or a different one.
      </p>
    </Shell>
  );
}

function Row({ label, value, testid }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span style={{ color: "var(--gf-text-muted)" }}>{label}</span>
      <span className="font-medium text-right" style={{ color: "var(--gf-text)" }} data-testid={testid}>
        {value}
      </span>
    </div>
  );
}
